// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import '../styles/HomePage.css';
import filmsData from '../data/movies.json';
import screeningsData from '../data/screenings.json';
import newsData from '../data/news.json';
import WaveText from '../countdown/WaveText'; // Pour l'animation du scénario (si vous souhaitez l'utiliser)
import { slugify } from "../components/utils";


const HomePage = () => {
  // Section 1 : Films à l'affiche aujourd'hui ou à venir
  const today = new Date();
  const screeningsToday = screeningsData.filter(s => {
    const screeningDate = new Date(s.heure_de_sortie);
    return screeningDate.toDateString() === today.toDateString();
  });

  let filmsToday = [];
  if (screeningsToday.length > 0) {
    // Récupérer les films correspondant aux screenings d'aujourd'hui
    const filmIds = [...new Set(screeningsToday.map(s => s.filmId))];
    filmsToday = filmIds.map(id => filmsData.find(film => film.id === id));
  } else {
    // Sinon, afficher les films des prochaines séances
    const upcomingScreenings = screeningsData.filter(s => new Date(s.heure_de_sortie) > today);
    const filmIds = [...new Set(upcomingScreenings.map(s => s.filmId))];
    filmsToday = filmIds.map(id => filmsData.find(film => film.id === id));
  }

  // Section 2 : Bande annonce aléatoire parmi les 10 derniers films
  const recentFilms = [...filmsData].sort((a, b) => b.id - a.id).slice(0, 10);
  const randomTrailerFilm = recentFilms[Math.floor(Math.random() * recentFilms.length)];

  // Section 3 : Événements à venir dans les actualités
  const upcomingNews = newsData.filter(article => article.category === "Événement");

  return (
    <div className="home-page">
      {/* Section 1 : Slider de films */}
      <section className="daily-films">
        <h2>Films du jour</h2>
        <div className="films-slider">
          {filmsToday.map(film => (
            <div key={film.id} className="film-poster">
              <Link to={`/film/${slugify(film.titre)}`}>
                <img src={film.poster} alt={film.titre} />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Section 2 : Bande annonce aléatoire */}
      <section className="random-trailer">
        <h2>Bande annonce du moment</h2>
        <div className="trailer-container">
          <iframe
            width="560"
            height="315"
            src={randomTrailerFilm.bande_annonce}
            title="Bande annonce"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </section>

      {/* Section 3 : Événements à venir */}
      <section className="upcoming-news">
        <h2>Événements à venir</h2>
        <div className="news-list">
          {upcomingNews.map(article => (
            <div key={article.id} className="news-item">
              <img src={article.image} alt={article.title} />
              <div className="news-info">
                <h3>{article.title}</h3>
                <p>{article.excerpt}</p>
                <Link to={`/news/${article.id}`}>Lire la suite →</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 4 : Scénario animé*/}
      <section className="scenario-animation">
        <h2>La Genèse d'un Film</h2>
        <motion.p
          className="scenario-text"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1 }}
        >
          🎬 Dans l'obscurité feutrée d'une salle de cinéma, les premières images s'esquissent, révélant un monde où chaque ombre raconte une histoire fascinante...
        </motion.p>
        <motion.p
          className="scenario-text"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          🍿 Les projecteurs s'allument, et le rideau se lève sur une aventure épique, mêlant passion, émotion et un brin de magie cinématographique !
        </motion.p>
      </section>
    </div>
  );
};

const Homepage_deep = () => {
  const [screenplayText, setScreenplayText] = useState('');
  const [randomTrailer, setRandomTrailer] = useState('');

  // Section 1 : Films du jour ou prochaines sorties
  const today = new Date().toISOString().split(' ')[0];
  const dailyMovies = filmsData.filter(movie => movie.releaseDate === today);
  const upcomingMovies = filmsData.sort((a, b) => new Date(a.releaseDate) - new Date(b.releaseDate));

  // Section 2 : Bande-annonce aléatoire
  useEffect(() => {
    const recentMovies = filmsData.slice(-10);
    const randomMovie = recentMovies[Math.floor(Math.random() * recentMovies.length)];
    setRandomTrailer(randomMovie?.bande_annonce || '');
  }, []);

  // Section 3 : Événements à venir
  const upcomingEvents = newsData
  .filter(event => new Date(event.date) > new Date() & event.category === "Événements")
  .sort((a, b) => new Date(a.date) - new Date(b.date))
  .slice(0, 3);
  // const upcomingEvents = newsData.filter(article => article.category === "Événements");
  // Section 4 : Animation de scénario
  const screenplayContent = `🎬 INT. BUREAU DE PRODUCTION - JOUR\nUn développeur passionné de cinéma tape furieusement sur son clavier, entouré d'écrans remplis de code. Des posters de films classiques décorent les murs.\n\n💡 CLOSE SUR L'ÉCRAN\nL'interface d'une application cinéma prend vie, des composants React dansent en parfaite harmonie. "Enfin...", murmure-t-il avec un sourire satisfait.\n\n\n🎬 Dans l'obscurité feutrée d'une salle de cinéma, les premières images s'esquissent, révélant un monde où chaque ombre raconte une histoire fascinante...\n\n🍿 Les projecteurs s'allument, et le rideau se lève sur une aventure épique, mêlant passion, émotion et un brin de magie cinématographique !`;

  useEffect(() => {
    let currentIndex = 0;
    const typingEffect = setInterval(() => {
      setScreenplayText(prev => screenplayContent.slice(0, currentIndex));
      if(currentIndex++ >= screenplayContent.length) clearInterval(typingEffect);
    }, 50);

    return () => clearInterval(typingEffect);
  }, []);

    // Ajouter ce state en haut du composant
    const [currentMovie, setCurrentMovie] = useState(0);

    // Ajouter cet effet pour l'auto-play
    useEffect(() => {
      const interval = setInterval(() => {
        setCurrentMovie(prev => (prev + 1) % 3);
      }, 5000); // Change toutes les 5 secondes

      return () => clearInterval(interval);
    }, []);

  return (
    <div className="homepage-container">
      {/* Section Films du jour */}
      <section className="upcoming-movies">
      <h2>Prochaines sorties en exclusivité dans vos salles de ciné</h2>
      <div className="slider-container">
        <AnimatePresence mode='wait'>
          {upcomingMovies.slice(0, 3).map((movie, index) => (
            currentMovie === index && (
              <motion.div
                key={movie.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="movie-slide"
              >
                <img src={movie.poster} alt={movie.titre} />
                <div className="slide-content">
                  <h3>{movie.titre}</h3>
                  <p className="realisateur">{movie.realisateur}</p>
                  <p className="sortie">Sortie en {movie.annee}</p>
                </div>
              </motion.div>
            )
          ))}
        </AnimatePresence>

    {/* Contrôles du slider */}
    <div className="slider-controls">
      {upcomingMovies.slice(0, 3).map((_, idx) => (
        <button
          key={idx}
          className={`slider-dot ${currentMovie === idx ? 'active' : ''}`}
          onClick={() => setCurrentMovie(idx)}
        />
      ))}
    </div>
  </div>
</section>


      {/* Section Bande-annonce aléatoire */}
      {randomTrailer && (
        <section className="random-trailer">
          <h2>Découverte du mois</h2>
          <div className="trailer-wrapper">
            <iframe
              src={randomTrailer}
              title="Bande-annonce aléatoire"
              allowFullScreen
            />
          </div>
        </section>
      )}

      {/* Section Événements à venir */}
      <section className="upcoming-events">
        <h2>Événements ciné à venir</h2>

        <div className="news-list">
          {upcomingEvents.map(article => (
            <div key={article.id} className="news-item">
              <img src={article.image} alt={article.title} />
              <div className="news-info">
                <h3>{article.title}</h3>
                <p>{article.excerpt}</p>
                <Link to={`/news/${article.id}`}>Lire la suite →</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section Animation scénario */}
      <section className="screenplay-animation">
        <div className="typewriter">
          {screenplayText}
          <span className="cursor">|</span>
        </div>
      </section>
    </div>
  );
};

export default Homepage_deep;
